/**
 * O RAMAL na tela: é por aqui que a voz do consultor entra e sai.
 *
 * Existe separado da tela de ligações por uma razão de produto: a ligação é
 * originada pelo servidor, e a central toca o RAMAL antes de chamar o cliente.
 * Se o ramal não estiver registrado, o "Ligar" da tela vira um botão que não
 * completa nada. Este componente é o que fecha esse laço, e por isso ele diz em
 * voz alta quando não está pronto, em vez de ficar quieto.
 */
import { useCallback } from "react";
import { Botao, Selo } from "@notes/ui";
import { Phone, PhoneOff, Mic, MicOff, PauseCircle, PlayCircle } from "lucide-react";
import { usarSoftphone, emChamada, type Credencial } from "@notes/softphone";
import { trpc } from "../lib/api.js";

const ROTULO_DO_REGISTRO: Record<string, { texto: string; tom: "bom" | "atencao" | "critico" }> = {
  desligado: { texto: "Ramal desligado", tom: "atencao" },
  registrando: { texto: "Conectando o ramal", tom: "atencao" },
  registrado: { texto: "Ramal pronto", tom: "bom" },
  sem_registro: { texto: "Ramal sem conexão", tom: "critico" },
};

const ROTULO_DA_CHAMADA: Record<string, string> = {
  ociosa: "", discando: "Chamando", tocando: "Tocando",
  em_conversa: "Em conversa", em_espera: "Em espera", encerrando: "Encerrando",
};

export function Ramal({ ativo }: { ativo: boolean }) {
  /**
   * A senha do ramal NUNCA é guardada: ela é buscada, vive em memória e vence
   * em minutos. É o oposto do que o mercado faz, que é carregar o ramal por
   * uma URL com token eterno.
   */
  const buscarCredencial = useCallback(async (): Promise<Credencial> => {
    const c = await trpc.voz.credencialDoRamal.mutate();
    if (!c.wss) throw new Error("a central de voz ainda não tem endereço configurado");
    return {
      usuario: c.usuario, senha: c.senha, dominio: c.dominio,
      wss: c.wss, expiraEm: new Date(c.expiraEm),
    };
  }, []);

  const fone = usarSoftphone({ ativo, buscarCredencial });
  const s = fone.situacao;
  const registro = ROTULO_DO_REGISTRO[s.registro] ?? ROTULO_DO_REGISTRO["desligado"]!;
  const falando = emChamada(s);

  return (
    <div className="enotes-ramal">
      <div className="enotes-ramal-estado">
        <Selo tom={registro.tom}>{registro.texto}</Selo>
        {falando && <span className="carimbo">{ROTULO_DA_CHAMADA[s.chamada]}</span>}
      </div>

      {falando && (
        <div className="enotes-ramal-botoes">
          {s.chamada === "tocando" && (
            <Botao tamanho="sm" variante="primario" onClick={() => void fone.atender()}>
              <Phone size={14} />Atender
            </Botao>
          )}
          <Botao tamanho="sm" variante="contorno" onClick={() => fone.mudo(!s.mudo)}>
            {s.mudo ? <MicOff size={14} /> : <Mic size={14} />}
            {s.mudo ? "Sem som" : "Mudo"}
          </Botao>
          <Botao tamanho="sm" variante="contorno"
            onClick={() => void fone.espera(s.chamada !== "em_espera")}>
            {s.chamada === "em_espera" ? <PlayCircle size={14} /> : <PauseCircle size={14} />}
            {s.chamada === "em_espera" ? "Retomar" : "Espera"}
          </Botao>
          <Botao tamanho="sm" variante="perigo" onClick={() => void fone.encerrar()}>
            <PhoneOff size={14} />Desligar
          </Botao>
        </div>
      )}

      {/* O aviso fala com a pessoa e diz o que fazer, não o que falhou. */}
      {s.aviso && <p className="enotes-dica-linha">{s.aviso}</p>}
    </div>
  );
}
